/* eslint-disable no-useless-assignment */
import { sldMap } from './data/sldMap';

/**
 * Validates if a string is a valid domain name, supporting wildcard domains
 * with specific formatting rules.
 *
 * Wildcard rules:
 * - Only one wildcard (*) is allowed
 * - Wildcard must be at the beginning followed by a dot (*.example.com)
 * - Patterns like ankr.*, *.*.ankr.com are not allowed
 *

 * test cases:
 * *.ankr.com - allowed
 * *.1.ankr.com - allowed
 * ankr.* - not allowed
 * *.ankr.* - not allowed
 * *.*.ankr.com - not allowed
 */
export function isValidDomain(value: string) {
  if (typeof value !== 'string') return false;
  value = value.toLowerCase();

  // Remove trailing dot if present
  if (value.endsWith('.')) {
    value = value.slice(0, value.length - 1);
  }

  // Check maximum domain length
  const MAX_DOMAIN_LENGTH = 253;
  if (value.length > MAX_DOMAIN_LENGTH) {
    return false;
  }

  // Wildcard validation rules
  const wildcardCount = (value.match(/\*/g) || []).length;
  if (wildcardCount > 1) return false;
  if (wildcardCount === 1 && !value.startsWith('*.')) return false;

  // Character set validation for entire domain
  const VALID_DOMAIN_CHARS = /^([\u0E00-\u0E7Fa-z0-9-._*]+)$/;
  if (!VALID_DOMAIN_CHARS.test(value)) {
    return false;
  }

  // Regular expressions for domain components
  const TLD_REGEX = /^(?:xn--)?(?!^\d+$)[\u0E00-\u0E7Fa-z0-9]+$/i; // TLD validation
  const STANDARD_LABEL_REGEX = /^([\u0E00-\u0E7Fa-zA-Z0-9-]+)$/; // Standard DNS label
  const RELAXED_LABEL_REGEX = /^([\u0E00-\u0E7Fa-zA-Z0-9-_]+)$/; // Label with underscore support
  const MAX_LABEL_LENGTH = 63;

  // Special handling for wildcard domains (*.example.com)
  const isWildcardDomain = value.startsWith('*.');
  if (isWildcardDomain) {
    const domainWithoutWildcard = value.substring(2);

    // Wildcard domain must contain at least one dot
    if (!domainWithoutWildcard.includes('.')) return false;

    const restLabels = domainWithoutWildcard.split('.');
    const restTld = restLabels.pop();

    // Validate TLD part
    if (restTld && !TLD_REGEX.test(restTld)) return false;

    // Validate remaining labels
    return restLabels.every(function (label) {
      return (
        STANDARD_LABEL_REGEX.test(label) &&
        label.length <= MAX_LABEL_LENGTH &&
        !label.startsWith('-') &&
        !label.endsWith('-')
      );
    });
  }

  // Second-level domain pattern (e.g., co.uk in example.co.uk)
  const SLD_REGEX = /(.*)\.(([\u0E00-\u0E7Fa-z0-9]+)(\.[a-z0-9]+))/;
  const matches = value.match(SLD_REGEX);

  let tld = null;
  let labels = null;

  // Handle second-level domains (like .co.uk)
  if (matches && matches.length > 2) {
    if (sldMap[matches[2] as string]) {
      tld = matches[2];
      labels = matches[1].split('.');
    }
  }

  // Standard domain processing
  if (!labels) {
    labels = value.split('.');
    if (labels.length <= 1) return false;

    tld = labels.pop();
    if (tld && !TLD_REGEX.test(tld)) return false;
  }

  // Validate each domain label
  const isValid = labels.every(function (label, index) {
    // Allow underscores except in the last label before TLD
    const validLabelChars =
      index === labels.length - 1 ? STANDARD_LABEL_REGEX : RELAXED_LABEL_REGEX;

    // Validate IDN (Internationalized Domain Name) format
    const DOUBLE_DASH_PATTERN = /--(--)?/g;
    const XN_DASH_PATTERN = /xn--/g;
    const doubleDashCount = (label.match(DOUBLE_DASH_PATTERN) || []).length;
    const xnDashCount = (label.match(XN_DASH_PATTERN) || []).length;

    // Punycode validation: double-dash count should match xn-- count
    if (index === labels.length - 1 && doubleDashCount !== xnDashCount) {
      return false;
    }

    return (
      validLabelChars.test(label) &&
      label.length <= MAX_LABEL_LENGTH &&
      !label.startsWith('-') &&
      !label.endsWith('-')
    );
  });

  return isValid;
}
